import { apiUrl } from '../utils/apiBase';
import React, { useState, useEffect } from 'react';
import { usePlaylistManager } from '../hooks/usePlaylistManager';
import { useAuth } from '../contexts/AuthContext';
import { 
  Upload, 
  X, 
  AlertTriangle, 
  Folder, 
  FileText, 
  Calendar, 
  Hash, 
  Trash2, 
  CheckCircle2, 
  Circle,
  Music,
  Check,
  Users,
  ChevronDown,
  Download,
  Heart,
  Edit3,
  Save,
  RotateCcw
} from 'lucide-react';

const PlaylistManager = ({ dark, onClose, onPlaylistsChange }) => {
  const { user: currentUser } = useAuth();
  const [selectedUser, setSelectedUser] = useState(null);
  
  const {
    playlists,
    selectedPlaylists,
    availableUsers,
    loading,
    error,
    apiAvailable,
    uploadPlaylist,
    uploadPlaylistFromUrl,
    togglePlaylist,
    deletePlaylist,
    downloadPlaylist,
    renamePlaylist,
    refreshPlaylistFromUrl,
    clearError,
    loadPlaylists,
    loadAvailableUsers,
    checkApiAvailability
  } = usePlaylistManager(selectedUser);

  const [uploading, setUploading] = useState(false);
  const [uploadingUrl, setUploadingUrl] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingName, setEditingName] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [allFiles, setAllFiles] = useState([]);
  const [favoritesFile, setFavoritesFile] = useState(null);
  const [playlistUrl, setPlaylistUrl] = useState('');

  // Carica file dei favoriti per l'utente corrente
  const loadFavoritesFile = async () => {
    try {
      const targetUser = selectedUser || currentUser?.username;
      if (!targetUser) return;

      const response = await fetch(apiUrl(`/uploads/favorites/${targetUser}.m3u`));
      if (response.ok) {
        const content = await response.text();
        const lines = content.split('\n');
        const channelCount = lines.filter(line => line.startsWith('#EXTINF:')).length;
        
        setFavoritesFile({
          id: 'favorites',
          originalName: 'preferiti.m3u',
          fileName: `${targetUser}.m3u`,
          filePath: `/uploads/favorites/${targetUser}.m3u`,
          uploadDate: new Date().toISOString(),
          channelCount,
          selected: false,
          isFavorites: true
        });
      } else {
        setFavoritesFile(null);
      }
    } catch (error) {
      console.warn('Errore caricamento file preferiti:', error);
      setFavoritesFile(null);
    }
  };

  // Combina playlist e file preferiti
  React.useEffect(() => {
    const combined = [...playlists];
    if (favoritesFile) {
      combined.unshift(favoritesFile); // Aggiungi preferiti all'inizio
    }
    setAllFiles(combined);
  }, [playlists, favoritesFile]);

  // Carica file preferiti quando cambia utente
  React.useEffect(() => {
    loadFavoritesFile();
  }, [selectedUser, currentUser]);

  // Funzione per scaricare un file
  const handleDownload = async (file) => {
    try {
      clearError();
      await downloadPlaylist(file.id, selectedUser);
    } catch (error) {
      console.error('Errore download:', error);
    }
  };

  // Funzione per rinominare un file
  const handleRename = async (fileId, newName) => {
    try {
      if (fileId === 'favorites') {
        // Non permettiamo la rinomina per i preferiti
        setEditingName(null);
        return;
      }

      clearError();
      await renamePlaylist(fileId, newName, selectedUser);
      setEditingName(null);
      
      // Ricarica la lista
      await loadPlaylists(selectedUser);
      
    } catch (error) {
      console.error('Errore rinomina:', error);
      setEditingName(null);
    }
  };

  // Gestione click su nome per rinomina
  const handleNameClick = (file) => {
    if (file.isFavorites) return; // Non permettere rinomina preferiti
    setEditingName(file.id);
    setEditingValue(file.originalName);
  };

  // Conferma rinomina
  const confirmRename = () => {
    if (editingValue.trim()) {
      handleRename(editingName, editingValue.trim());
    } else {
      cancelRename();
    }
  };

  const cancelRename = () => {
    setEditingName(null);
    setEditingValue('');
  };

  // Il caricamento delle playlist è gestito automaticamente dall'hook usePlaylistManager
  // quando cambia selectedUser

  // Gestione upload file
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('Inizio upload file:', file.name, 'Dimensione:', file.size, 'bytes');

    try {
      setUploading(true);
      clearError();
      
      await uploadPlaylist(file);
      
      console.log('Upload completato con successo');
      
      // Notifica il parent component che le playlist sono cambiate
      if (onPlaylistsChange) {
        onPlaylistsChange();
      }
      
      // Reset input
      event.target.value = '';
    } catch (err) {
      console.error('Errore upload:', err);
      // L'errore è già gestito dal hook, ma aggiungiamo log per debug
    } finally {
      setUploading(false);
    }
  };

  // Gestione upload da URL
  const handleUrlUpload = async () => {
    if (!playlistUrl) return;
    try {
      setUploadingUrl(true);
      clearError();
      await uploadPlaylistFromUrl(playlistUrl);
      setPlaylistUrl('');
      if (onPlaylistsChange) {
        onPlaylistsChange();
      }
    } catch (err) {
      console.error('Errore upload URL:', err);
    } finally {
      setUploadingUrl(false);
    }
  };

  // Gestione toggle playlist
  const handleToggle = async (playlistId) => {
    try {
      clearError();
      await togglePlaylist(playlistId, selectedUser);
      
      // Notifica il parent component che le playlist sono cambiate (stato selezione)
      if (onPlaylistsChange) {
        onPlaylistsChange();
      }
    } catch (err) {
      // Errore già gestito dal hook
    }
  };

  // Gestione eliminazione
  const handleDelete = async (playlistId) => {
    try {
      clearError();
      await deletePlaylist(playlistId, selectedUser);
      setDeleteConfirm(null);
      
      // Notifica il parent component che le playlist sono cambiate
      if (onPlaylistsChange) {
        onPlaylistsChange();
      }
    } catch (err) {
      // Errore già gestito dal hook
    }
  };

  // Gestione refresh playlist da URL
  const handleRefresh = async (playlistId) => {
    try {
      clearError();
      await refreshPlaylistFromUrl(playlistId);
      if (onPlaylistsChange) {
        onPlaylistsChange();
      }
    } catch (err) {
      console.error('Errore refresh playlist:', err);
    }
  };

  // Formatta data
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className={`modal-container w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col rounded-2xl border border-white/10 shadow-2xl transition-all duration-200 ${
        dark
          ? ''
          : ''
      }`} style={{
        background: 'linear-gradient(135deg, #2a0845 0%, #6441a5 100%)',
        color: '#fff',
        backdropFilter: 'blur(16px)',
        boxShadow: '0 4px 32px #0008',
        borderRadius: 20,
        fontSize: '70%'
      }}>
        {/* Header */}
        <div className="mobile-sticky-header flex-shrink-0 mobile-reduced-padding p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/30 to-pink-500/30 flex items-center justify-center border border-purple-400/30">
                <Music size={24} className="text-purple-200" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  Gestione Playlist M3U
                  {selectedUser && currentUser?.role === 'admin' && (
                    <span className="text-sm bg-yellow-500/20 text-yellow-200 px-2 py-1 rounded-full border border-yellow-400/30">
                      {selectedUser}
                    </span>
                  )}
                </h2>
                <p className="text-sm mt-1 text-white/70 flex items-center gap-2">
                  <FileText size={16} className="text-purple-300" />
                  {playlists.length} playlist totali • {selectedPlaylists.length} selezionate
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl transition-all duration-200 hover:bg-white/10 text-white/60 hover:text-white group"
              title="Chiudi"
            >
              <X size={20} className="group-hover:rotate-90 transition-transform duration-200" />
            </button>
          </div>
          
          {/* Selettore Utente per Admin */}
          {currentUser?.role === 'admin' && availableUsers.length > 0 && (
            <div className="mt-4 p-4 bg-white/5 rounded-xl border border-white/10">
              <div className="flex items-center gap-3">
                <Users size={20} className="text-blue-300" />
                <div className="flex-1">
                  <label className="block text-sm font-medium text-white/90 mb-2">
                    Gestisci playlist di:
                  </label>
                  <div className="relative">
                    <select
                      value={selectedUser || ''}
                      onChange={(e) => setSelectedUser(e.target.value || null)}
                      className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:border-blue-400/50 focus:ring-1 focus:ring-blue-400/30 transition-colors appearance-none pr-8"
                    >
                      <option value="" className="bg-gray-800">Tutte le mie playlist</option>
                      {availableUsers.map(user => (
                        <option key={user.username} value={user.username} className="bg-gray-800">
                          {user.username} ({user.playlistCount} playlist, {user.selectedCount} selezionate)
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white/60 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
            {/* Upload Section - Solo per le proprie playlist */}
            {apiAvailable && !selectedUser && (
              <div className="flex-shrink-0 mobile-reduced-padding p-4 border-b border-white/10">
                <div className="flex gap-2">
                  <label className="relative cursor-pointer group">
                    <input
                      type="file"
                      accept=".m3u,.m3u8"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className={`p-2 rounded-lg transition-all duration-200 ${
                      uploading
                        ? 'bg-white/10 cursor-not-allowed text-white/60'
                        : 'bg-white/5 hover:bg-white/10 text-white/90'
                    }`}>
                      {uploading ? (
                        <div className="animate-spin w-5 h-5 border-2 border-purple-400 border-t-transparent rounded-full" />
                      ) : (
                        <Upload size={20} className="text-blue-300" />
                      )}
                    </div>
                  </label>
                  <input
                    type="text"
                    value={playlistUrl}
                    onChange={(e) => setPlaylistUrl(e.target.value)}
                    placeholder="URL playlist M3U/M3U8"
                    className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white focus:border-blue-400/50 focus:ring-1 focus:ring-blue-400/30"
                  />
                  <button
                    onClick={handleUrlUpload}
                    disabled={uploadingUrl || !playlistUrl}
                    className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm disabled:opacity-50"
                  >
                    {uploadingUrl ? 'Caricamento...' : 'Carica URL'}
                  </button>
                </div>
              </div>
            )}
        
        {/* Info Message per Admin che visualizza playlist altrui */}
        {selectedUser && currentUser?.role === 'admin' && (
          <div className="flex-shrink-0 mx-6 mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
            <div className="flex items-center gap-3">
              <Users size={20} className="text-blue-400 flex-shrink-0" />
              <div>
                <span className="text-blue-200 font-medium">
                  Stai visualizzando le playlist di <strong>{selectedUser}</strong>
                </span>
                <p className="text-xs text-blue-300/70 mt-1">
                  Puoi attivare/disattivare ed eliminare playlist. L'upload è disponibile solo per le tue playlist.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <div className="flex-shrink-0 mx-6 mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle size={20} className="text-red-400 flex-shrink-0" />
                <span className="text-red-200 font-medium">{error}</span>
              </div>
              <button
                onClick={clearError}
                className="text-red-300 hover:text-red-100 p-1 rounded-lg hover:bg-red-500/20 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto min-h-0 touch-scroll">
          {!apiAvailable ? (
            /* Messaggio quando API non disponibili */
            <div className="text-center p-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center border border-yellow-400/30">
                <AlertTriangle size={32} className="text-yellow-400" />
              </div>
              <h3 className="text-lg font-bold mb-2 text-yellow-400">
                Gestione Playlist Non Disponibile
              </h3>
              <p className="text-sm text-gray-400 mb-2">
                Le API per la gestione delle playlist non sono raggiungibili in questo ambiente.
              </p>
              <p className="text-xs text-gray-500">
                Usa l'upload diretto M3U dal menu principale per caricare le playlist.
              </p>
            </div>
          ) : loading && allFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12">
              <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mb-4" />
              <span className="text-gray-400 font-medium">Caricamento playlist...</span>
            </div>
          ) : allFiles.length === 0 ? (
            <div className="text-center p-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-br from-gray-500/20 to-gray-600/20 flex items-center justify-center border border-gray-400/30">
                <Folder size={32} className="text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-400 mb-2">Nessuna playlist caricata</h3>
              <p className="text-sm text-gray-500">
                Carica il tuo primo file M3U per iniziare
              </p>
            </div>
          ) : (
            <div className="mobile-table-container overflow-hidden">
              <table className="w-full">
                <thead className="sticky top-0 bg-gradient-to-r from-purple-900/50 to-pink-900/50 backdrop-blur-sm border-b border-white/10">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-purple-200">
                    <th className="p-2 w-12"></th>
                    <th className="p-4">
                      <div className="flex items-center gap-2">
                        <FileText size={14} />
                        Nome File
                      </div>
                    </th>
                    <th className="p-4">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} />
                        Data Upload
                      </div>
                    </th>
                    <th className="p-4">
                      <div className="flex items-center gap-2">
                        <Hash size={14} />
                        Canali
                      </div>
                    </th>
                    <th className="p-4">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {allFiles
                    .sort((a, b) => {
                      // Favoriti sempre in cima
                      if (a.isFavorites) return -1;
                      if (b.isFavorites) return 1;
                      return new Date(b.uploadDate) - new Date(a.uploadDate);
                    })
                    .map((file) => (
                    <tr key={file.id} className="hover:bg-white/5 transition-colors duration-200 group">
                      <td className="p-2 w-12">
                        {file.isFavorites ? (
                          <div className="flex items-center justify-center w-5 h-5">
                            <Heart size={16} className="text-red-400" />
                          </div>
                        ) : (
                          <button
                            onClick={() => handleToggle(file.id)}
                            className="flex items-center justify-center w-5 h-5 rounded-lg transition-all duration-200"
                          >
                            {file.selected ? (
                              <CheckCircle2 size={20} className="text-green-400" />
                            ) : (
                              <Circle size={20} className="text-gray-400 group-hover:text-white" />
                            )}
                          </button>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${
                            file.isFavorites 
                              ? 'bg-gradient-to-br from-red-500/20 to-pink-500/20 border-red-400/30'
                              : 'bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-blue-400/30'
                          }`}>
                            {file.isFavorites ? (
                              <Heart size={16} className="text-red-300" />
                            ) : (
                              <Music size={16} className="text-blue-300" />
                            )}
                          </div>
                          
                          {editingName === file.id ? (
                            <div className="flex items-center gap-2 flex-1">
                              <input
                                type="text"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') confirmRename();
                                  if (e.key === 'Escape') cancelRename();
                                }}
                                className="px-2 py-1 bg-gray-800 border border-gray-600 rounded text-white text-sm flex-1"
                                autoFocus
                              />
                              <button
                                onClick={confirmRename}
                                className="p-1 text-green-400 hover:text-green-300"
                              >
                                <Check size={14} />
                              </button>
                              <button
                                onClick={cancelRename}
                                className="p-1 text-red-400 hover:text-red-300"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleNameClick(file)}
                              className={`font-medium text-white truncate text-left hover:text-blue-300 transition-colors ${
                                file.isFavorites ? 'cursor-default hover:text-white' : 'cursor-pointer'
                              }`}
                              disabled={file.isFavorites}
                            >
                              {file.originalName}
                              {!file.isFavorites && (
                                <Edit3 size={12} className="inline ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-sm text-gray-400">
                        {formatDate(file.uploadDate)}
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                          file.isFavorites
                            ? 'bg-red-500/20 text-red-200 border-red-400/30'
                            : 'bg-purple-500/20 text-purple-200 border-purple-400/30'
                        }`}>
                          {file.channelCount}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {/* Download Button */}
                          <button
                            onClick={() => handleDownload(file)}
                            className="p-1.5 rounded-lg text-sm font-medium text-blue-300 hover:text-blue-200 hover:bg-blue-500/20 transition-all duration-200 border border-blue-500/30 hover:border-blue-400/50"
                            title="Scarica file"
                          >
                            <Download size={14} />
                          </button>

                          {/* Refresh Button */}
                          {file.sourceUrl && (
                            <button
                              onClick={() => handleRefresh(file.id)}
                              className="p-1.5 rounded-lg text-sm font-medium text-green-300 hover:text-green-200 hover:bg-green-500/20 transition-all duration-200 border border-green-500/30 hover:border-green-400/50"
                              title="Aggiorna da URL"
                            >
                              <RotateCcw size={14} />
                            </button>
                          )}
                          
                          {/* Delete Button - Solo per playlist, non per favoriti */}
                          {!file.isFavorites && (
                            <button
                              onClick={() => setDeleteConfirm(file.id)}
                              className="p-1.5 rounded-lg text-sm font-medium text-red-300 hover:text-red-200 hover:bg-red-500/20 transition-all duration-200 border border-red-500/30 hover:border-red-400/50"
                              title="Elimina playlist"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="mobile-footer flex-shrink-0 mobile-reduced-padding p-6 border-t border-white/10 bg-gradient-to-r from-purple-900/20 to-pink-900/20">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 text-sm text-white/70">
              <CheckCircle2 size={16} className="text-green-400" />
              {allFiles.length > 0 ? (
                <>
                  {allFiles.length} file totali 
                  {favoritesFile && (
                    <span className="text-red-300">• Include preferiti</span>
                  )}
                  • {selectedPlaylists.length} playlist selezionate
                </>
              ) : (
                'Le playlist selezionate saranno caricate automaticamente nell\'app'
              )}
            </div>
            <button
              onClick={onClose}
              className="mobile-button inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold transition-all duration-200 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl hover:scale-105"
            >
              <Check size={18} />
              Conferma
            </button>
          </div>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="max-w-md mx-4 p-6 rounded-2xl border border-white/10 shadow-2xl" style={{
            background: 'linear-gradient(135deg, #2a0845 0%, #6441a5 100%)',
            color: '#fff',
            backdropFilter: 'blur(16px)',
            boxShadow: '0 4px 32px #0008',
            borderRadius: 20
          }}>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500/20 to-pink-500/20 flex items-center justify-center border border-red-400/30">
                <AlertTriangle size={24} className="text-red-400" />
              </div>
              <h3 className="text-lg font-bold">Conferma Eliminazione</h3>
            </div>
            <p className="mb-6 text-sm text-white/80">
              Sei sicuro di voler eliminare questa playlist? L'azione non può essere annullata.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 bg-white/10 hover:bg-white/20 text-white border border-white/20 hover:border-white/30"
              >
                <X size={16} />
                Annulla
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Trash2 size={16} />
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaylistManager;
